import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    DateRangePicker,
    FormStore,
    inject,
    NothingHere,
    SubTopBar,
    TasksStore,
} from 'Mri.Web.Common';
import { stringify } from 'qs';
import * as React from 'react';
import { BsFillGridFill } from '@react-icons/all-files/bs/BsFillGridFill';
import { BsListUl } from '@react-icons/all-files/bs/BsListUl';
import { BsTv } from '@react-icons/all-files/bs/BsTv';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { DropdownItem, DropdownMenu, DropdownToggle, Form, UncontrolledButtonDropdown } from 'reactstrap';
import { AppStore } from '../../Core/AppStore';
import { DisplayEventsList } from '../DisplayEventsList';
import { DisplaysStore } from '../DisplaysStore';

export interface IDisplayEventLogParams {
    serialNumber: string;
}
export interface IDisplayEventLogCardsProps extends RouteComponentProps<IDisplayEventLogParams> {}

@autoinject
@observer
export class DisplayEventLogCards extends React.Component<IDisplayEventLogCardsProps, any> {
    public constructor(props: IDisplayEventLogCardsProps) {
        super(props);
        this.state = {};

        const startTime = this.appStore.storage.displayLogTimes?.start
            ? new Date(this.appStore.storage.displayLogTimes?.start)
            : this.formStore.fields.startTime.value;
        if (!this.appStore.storage.displayLogTimes?.start) {
            startTime.setMonth(startTime.getMonth() - 1);
        } else {
            this.formStore.fields.startTime.setValue(startTime);
        }

        const endTime = this.appStore.storage.displayLogTimes?.end
            ? new Date(this.appStore.storage.displayLogTimes?.end)
            : this.formStore.fields.endTime.value;
        if (!this.appStore.storage.displayLogTimes?.end) {
            endTime.setDate(endTime.getDate() + 1);
            endTime.setMinutes(59);
            endTime.setSeconds(59);
        } else {
            this.formStore.fields.endTime.setValue(endTime);
        }
    }

    @action
    public async componentDidMount() {
        this.loading = true;

        try {
            await this.displaysStore.loadAsync(this.props.match.params.serialNumber);
            await this.getEventLogAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div>
                <SubTopBar>
                    <Form onSubmit={this.onSubmit}>
                        <div className="d-block w-100">
                            <DateRangePicker
                                startDate={this.formStore.fields.startTime}
                                endDate={this.formStore.fields.endTime}
                                className="d-inline-block me-2"
                                timezone={this.displaysStore.timezones.get(this.props.match.params.serialNumber)}
                            />

                            <button className="mx-2 btn btn-primary">{this.loading ? 'Loading...' : 'Apply'}</button>

                            <a className="btn btn-link" onClick={this.exportCsvUrlAsync}>
                                Export to CSV
                            </a>
                        </div>
                    </Form>

                    <UncontrolledButtonDropdown className="ms-auto">
                        <DropdownToggle caret size="lg" color="link">
                            <BsFillGridFill />
                        </DropdownToggle>

                        <DropdownMenu end>
                            <LinkContainer to={`/displays/${this.props.match.params.serialNumber}/event-log/cards`}>
                                <DropdownItem>
                                    <BsFillGridFill /> Card View
                                </DropdownItem>
                            </LinkContainer>
                            <LinkContainer to={`/displays/${this.props.match.params.serialNumber}/event-log/list`}>
                                <DropdownItem>
                                    <BsListUl className="" /> List View
                                </DropdownItem>
                            </LinkContainer>
                        </DropdownMenu>
                    </UncontrolledButtonDropdown>
                </SubTopBar>

                <div className="container-fluid pt-3">
                    {this.displayEvents && (
                        <DisplayEventsList
                            displayEvents={this.displayEvents}
                            serialNumber={this.props.match.params.serialNumber}
                        />
                    )}
                    <NothingHere
                        empty={!this.displayEvents?.length}
                        icon={<BsTv />}
                        loading={this.loading}
                        label="No Display Events"
                        loadingMessage="Loading Events..."
                    />
                </div>
            </div>
        );
    }

    @bind
    private onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        this.getEventLogAsync();
    }

    @bind
    @action
    private async getEventLogAsync() {
        this.loading = true;

        this.appStore.storage.displayLogTimes = {
            start: this.formStore.fields.startTime.value,
            end: this.formStore.fields.endTime.value,
        };

        try {
            const events = await this.displaysStore.getEventHistoryAsync(
                this.props.match.params.serialNumber,
                this.formStore.values.startTime,
                this.formStore.values.endTime
            );
            if (events) this.displayEvents = events;
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    private async exportCsvUrlAsync() {
        const query = stringify({
            startTime: this.formStore.values.startTime,
            endTime: this.formStore.values.endTime,
        });
        const task = this.tasksStore.startSessionTask('Export Display Events');

        try {
            await this.displaysStore.exportEventLogAsync(this.props.match.params.serialNumber, query);
            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    private formStore = new FormStore({
        startTime: new Date(),
        endTime: new Date(),
    });

    @observable
    private loading = false;
    @observable
    private displayEvents?: Mri.DisplayEvents.DisplayEvent[];
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private tasksStore!: TasksStore;
}
